import type { ReactNode } from 'react';
import { CheckCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import classNames from 'styles/utils/classNames';

interface InfoAlertProps {
  title?: string;
  desc?: string;
  className?: string;
  variant?: 'brand' | 'neutral' | 'error' | 'info';
  iconType?: 'info' | 'check';
  /** Rendered under the description — e.g. an action link. */
  children?: ReactNode;
  /** Shows a × button that calls this. */
  onDismiss?: () => void;
}

const ICONS: Record<NonNullable<InfoAlertProps['iconType']>, typeof InformationCircleIcon> = {
  info: InformationCircleIcon,
  check: CheckCircleIcon,
};

const CONTAINER_CLASSES: Record<NonNullable<InfoAlertProps['variant']>, string> = {
  brand: 'bg-color-bg-lightmode-brand-secondary',
  error: 'bg-color-bg-lightmode-error',
  info: 'bg-[#f6fbfe] dark:bg-[#1e4bab]/20',
  neutral: 'bg-color-bg-lightmode-secondary dark:bg-color-bg-darkmode-secondary',
};

const ICON_CLASSES: Record<NonNullable<InfoAlertProps['variant']>, string> = {
  brand: 'text-color-brand-primary',
  error: 'text-color-error',
  info: 'text-[#1e4bab] dark:text-[#a9c2f5]',
  neutral: 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
};

const TITLE_CLASSES: Record<NonNullable<InfoAlertProps['variant']>, string> = {
  brand: 'text-color-brand-primary',
  error: 'text-color-error',
  info: 'text-[#1e4bab] dark:text-[#a9c2f5]',
  neutral: 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
};

const DESC_CLASSES: Record<NonNullable<InfoAlertProps['variant']>, string> = {
  brand: 'text-color-text-brand',
  error: 'text-color-error',
  info: 'text-[#1e4bab] dark:text-[#a9c2f5]',
  neutral: 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
};

export default function InfoAlert({
  title,
  desc,
  className,
  variant = 'brand',
  iconType = 'info',
  children,
  onDismiss,
}: InfoAlertProps) {
  const Icon = ICONS[iconType];
  return (
    <div
      className={classNames('flex gap-3 rounded-xl p-ds-lg', CONTAINER_CLASSES[variant], className)}
    >
      <Icon className={classNames('h-5 w-5 shrink-0', ICON_CLASSES[variant])} aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title && (
          <p className={classNames('typography-product-body-highlight', TITLE_CLASSES[variant])}>
            {title}
          </p>
        )}
        {desc && (
          <p className={classNames('typography-product-caption', DESC_CLASSES[variant])}>{desc}</p>
        )}
        {children}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className={classNames('shrink-0 self-start', ICON_CLASSES[variant])}
        >
          <XMarkIcon className="h-5 w-5" aria-hidden />
        </button>
      )}
    </div>
  );
}
